import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, take, takeUntil } from 'rxjs';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { IFormKey, IFormSelectOption } from 'src/app/modules/shared/models/forms';
import { LoggerService } from 'src/app/services/logger.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { ESpecificBeneficiaryType } from '../beneficiary-form-isv/beneficiary-form-isv.constants';
import { isv_tutor_proper_verification_form_template, isv_tutor_proper_verification_form_validators } from '../contractor-change-isv/contractor-change-isv.constants';
import { document_change_form_validators } from '../document-change/document-change.constants';
import { effective_owner_registry_form_validators, isv_contractor_residence_form_validators } from '../registry-form/registry-form.constants';
import { RegistrySearchIsvComponent } from '../registry-search-isv/registry-search-isv.component';
import { EModalOutput, IModalButton, REMOVE_BUTTON } from './../../../shared/components/basic-modal/basic-modal.constants';
import { beneficiary_form_keys, beneficiary_form_validators, beneficiary_not_designated_fields, beneficiary_PF_fields, beneficiary_PG_fields, effective_owner_positional_text, insured_form_keys, operation_data_form_keys, operation_purpose_form_key, operator_data_form_keys } from './registry-recap.constants';
import { IsvUtils } from 'src/app/utils/IsvUtils';

@Component({
  selector: 'app-registry-recap',
  templateUrl: './registry-recap.component.html',
  styleUrl: './registry-recap.component.scss'
})
export class RegistryRecapComponent implements OnInit, OnDestroy {

  @Input() isReadOnly: boolean;

  @Input() contractorRegistryForm: FormGroup;
  @Input() contractorResidenceForm: FormGroup;
  @Input() contractorAddressForm: FormGroup;
  @Input() contractorIdentificationDocumentForm: FormGroup;
  @Input() contractorBusinessForm: FormGroup;
  @Input() properVerificationForm: FormGroup;
  @Input() consentsForm: FormGroup;

  @Input() operationForm: FormGroup;
  @Input() operatorForm: FormGroup;
  
  @Input() legalGuardianRegistryForm: FormGroup;
  @Input() legalGuardianResidenceForm: FormGroup;
  @Input() legalGuardianAddressForm: FormGroup;
  @Input() legalGuardianIdentificationDocumentForm: FormGroup;
  @Input() legalGuardianProperVerificationForm: FormGroup;

  @Input() effectiveOwnersList: any[];

  @Input() insuredForm: FormGroup;
  @Input() beneficiaryForm: FormGroup;

  @Input() saeList: IFormSelectOption[];
  @Input() taeList: IFormSelectOption[];
  @Input() atecoList: IFormSelectOption[];
  @Input() activityTypes: IFormSelectOption[];
  @Input() countriesList: IFormSelectOption[];
  @Input() provincesList: IFormSelectOption[];
  @Input() placesList: IFormSelectOption[];
  @Input() documentTypeList: IFormSelectOption[];
  @Input() documentReleaseAuthorityList: IFormSelectOption[];
  @Input() documentationList: IFormSelectOption[];
  @Input() fundsOriginList: IFormSelectOption[];
  @Input() incomeLevelsList: IFormSelectOption[];
  @Input() executionModeList: IFormSelectOption[];
  @Input() operationExecutionModeList: IFormSelectOption[];
  @Input() agreementPurposeList: IFormSelectOption[];
  @Input() presenceInExternalList: IFormSelectOption[];
  @Input() beneficiariesClassification: IFormSelectOption[];
  @Input() businessTypes: IFormSelectOption[];
  @Input() relazioneContraenteBeneficiario: IFormSelectOption[];
  @Input() relazioneContraenteAssicurato: IFormSelectOption[];
  @Input() relazioneContraenteEsecutore: IFormSelectOption[];
  @Input() tipoDelega: IFormSelectOption[];
  @Input() productCategoriesList: IFormSelectOption[];
  @Input() riscattoTypesList: IFormSelectOption[];
  @Input() versamentoTypesList: IFormSelectOption[];
  @Input() customerBehaviourList: IFormSelectOption[];
  @Input() professionsList: IFormSelectOption[];
  @Input() incomeBracketsList: IFormSelectOption[];
  @Input() holderBeneficiaryRelationshipList: IFormSelectOption[] = null;
  @Input() insuredBeneficiaryRelationshipList: IFormSelectOption[] = null;
  @Input() chargeTypes: IFormSelectOption[] = null;
  @Input() operationPurposeList: IFormSelectOption[] = null;

  effectiveOwnersButtonList: IModalButton[] = [ REMOVE_BUTTON ];

  operationFormKeys: IFormKey[];
  operatorFormKeys: IFormKey[];

  insuredFormKeys: IFormKey[];

  beneficiaryFormKeys: IFormKey[];

  destroy$ = new Subject<void>();

  constructor(
    private readonly _policyService: PolicyService,
    private readonly _dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.manageForms();
    this.manageFormKeys();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  manageForms(): void {
    this.beneficiaryForm?.get('designatedBeneficiary')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.beneficiaryFormKeys?.forEach((key) => {
          if (!['beneficiaryNewLine', 'designatedBeneficiary'].includes(key.controlName)) {
            key.hidden = value !== 'NO'
              ? key.controlName !== 'beneficiaryType'
              : key.controlName !== 'description';
            this.beneficiaryForm?.get(key.controlName)?.setValidators(
              key.hidden ? null : beneficiary_form_validators[key.controlName]
            );
          }
        });
      });
    this.beneficiaryForm?.get('beneficiaryType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const allowedFields = value === ESpecificBeneficiaryType.PERSONA_FISICA
          ? beneficiary_PF_fields
          : beneficiary_PG_fields;
        this.beneficiaryFormKeys?.forEach((key) => {
          if (!['beneficiaryNewLine', 'designatedBeneficiary', 'beneficiaryType', 'description'].includes(key.controlName)) {
            key.hidden = !allowedFields.includes(key.controlName);
            this.beneficiaryForm?.get(key.controlName)?.setValidators(
              key.hidden ? null : beneficiary_form_validators[key.controlName]
            );
            this.beneficiaryForm?.get(key.controlName)?.setValue(null, { emitEvent: false });
          }
        });
      });
    this.operationForm?.get('fundOrigin')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value === 'ALTRO') {
          this.operationForm?.get('fundOriginNote')?.enable();
          this.operationForm?.get('fundOriginNote')?.setValidators([Validators.required, Validators.maxLength(200)]);
        } else {
          this.operationForm?.get('fundOriginNote')?.setValue(null);
          this.operationForm?.get('fundOriginNote')?.clearValidators();
          this.operationForm?.get('fundOriginNote')?.disable();
        }
        this.operationForm?.get('fundOriginNote')?.updateValueAndValidity();
      });

    const insuredFiscalCode = CommonUtils.checkAndGet(
        this._policyService.policyDetail,
        'insured.taxOrVatCode',
        null
    );
    const holderFiscalCode = CommonUtils.checkAndGet(
        this._policyService.policyDetail,
        'policyHolder.taxOrVatCode',
        null
    );
    const policyType = CommonUtils.checkAndGet(
        this._policyService.policyDetail,
        'summary.policyType',
        null
    );

    this.operationForm?.get('contractorNotInsured').setValue(
        policyType !== 'CAPITALIZZAZIONE' &&
        insuredFiscalCode !== holderFiscalCode
            ? 'SI'
            : 'NO'
    );
    this.operationForm?.get('designedBeneficiary').setValue('NO');
    this.operationForm?.get('designedBeneficiary').disable();
    this.operationForm?.get('contractorNotInsured').disable();
    if (insuredFiscalCode !== holderFiscalCode) {
        const insuredData = CommonUtils.checkAndGet(this._policyService.policyDetail, 'insured', null);
        this.insuredForm?.patchValue({
            name: CommonUtils.checkAndGet(insuredData, 'name', null),
            surname: CommonUtils.checkAndGet(insuredData, 'surname', null),
            fiscalCode: CommonUtils.checkAndGet(insuredData, 'taxOrVatCode', null),
            birthDate: CommonUtils.checkAndGet(insuredData, 'birthDate', null),
            birthPlace: CommonUtils.checkAndGet(insuredData, 'birthPlace', null)
        });
    }
  }

  manageFormKeys(): void {
    this.operationFormKeys = CommonUtils.assign(operation_data_form_keys);
    this.setOptions(this.operationFormKeys, 'productCategory', this.productCategoriesList);
    this.setOptions(this.operationFormKeys, 'additionalPaymentType', this.versamentoTypesList);
    this.setOptions(this.operationFormKeys, 'redeemType', this.riscattoTypesList);
    this.setOptions(this.operationFormKeys, 'liquidationType', [
      { key: 'Liquidazione', value: 'Liquidazione'}
    ]);
    this.setOptions(this.operationFormKeys, 'withdrawalType', [
      { key: 'Recesso', value: 'Recesso'}
    ]);
    this.setOptions(this.operationFormKeys, 'paymentType', [{ key: '08', value: 'Bonifico Bancario' }]);
    this.setOptions(this.operationFormKeys, 'fundOriginCountry', this.countriesList.map((country) => {
      return { key: country.value, value: country.value };
    }));
    this.setOptions(this.operationFormKeys, 'fundOrigin', this.fundsOriginList);
    this.setOptions(this.operationFormKeys, 'reportScope', this.agreementPurposeList);
    if (['_RISPA', '_RISTO'].includes(this._policyService.operationId)) {
      this.operationFormKeys.push(operation_purpose_form_key);
      this.setOptions(this.operationFormKeys, 'operationPurpose', this.operationPurposeList);
    }
    this.operatorFormKeys = CommonUtils.assign(operator_data_form_keys);
    this.documentationList.unshift({ key: '', value: null });
    this.setOptions(this.operatorFormKeys, 'collectedDocs', this.documentationList);
    if (this.legalGuardianRegistryForm && !this.isReadOnly) {
      // se il contraente è PG oppure se il soggetto ha un tutore legale
      this.operatorForm?.get('collectedDocs')?.enable();
    }

    this.insuredFormKeys = CommonUtils.assign(insured_form_keys);
    this.setOptions(this.operatorFormKeys, 'isPEP', this.presenceInExternalList);

    this.beneficiaryFormKeys = CommonUtils.assign(beneficiary_form_keys);
    this.setOptions(this.beneficiaryFormKeys, 'isPEP', this.presenceInExternalList);
    this.setOptions(this.beneficiaryFormKeys, 'beneficiaryContractorRelationship', this.relazioneContraenteBeneficiario) 
    this.setOptions(this.beneficiaryFormKeys, 'beneficiaryInsuredRelationship', this.relazioneContraenteAssicurato);
    if (this.beneficiaryForm && this.beneficiaryForm.get('designatedBeneficiary')?.value !== null) {
      this.beneficiaryFormKeys.forEach((key) => {
        if (['designatedBeneficiary', 'beneficiaryNewLine'].includes(key.controlName)) {
          key.hidden = false;
        } else if (beneficiary_not_designated_fields.includes(key.controlName)) {
          key.hidden = this.beneficiaryForm.get('designatedBeneficiary').value === 'SI';
        } else if (key.controlName === 'beneficiaryType') {
          key.hidden = this.beneficiaryForm.get('designatedBeneficiary').value === 'NO';
        } else {
          if (this.beneficiaryForm.get('designatedBeneficiary').value === 'NO') {
            key.hidden = !beneficiary_not_designated_fields.includes(key.controlName);
          } else {
            const isBeneficiaryPF = this.beneficiaryForm.get('beneficiaryType').value === ESpecificBeneficiaryType.PERSONA_FISICA;
            key.hidden = isBeneficiaryPF
              ? !beneficiary_PF_fields.includes(key.controlName)
              : !beneficiary_PG_fields.includes(key.controlName);
          }
        }
        this.beneficiaryForm.get(key.controlName)?.setValidators(
          key.hidden ? null : beneficiary_form_validators[key.controlName]
        );
      });
    }
  }

  setOptions(keys: IFormKey[], controlName: string, options: IFormSelectOption[]) {
    if (!keys) {
      return;
    }
    const key = keys.find(k => k.controlName === controlName);
    if (key) {
      key.options = options;
    }
  }

  addNewEffectiveOwner(): void {
    this._dialog.open(RegistrySearchIsvComponent, { disableClose: true })
      .afterClosed()
      .pipe(take(1))
      .subscribe((outcome) => {
        LoggerService.logInfo(this.constructor.name, 'addNewEffectiveOwner', outcome);
        if (outcome) {
          this.setNewEffectiveOwner(outcome)
        }
      });   
  }

  setNewEffectiveOwner(outcome: any): void {
    const registryTemplate = IsvUtils.initializeEffectiveOwnerRegistryFormTemplate(outcome);
    const fiscalAddress = CommonUtils.checkAndGet(outcome, 'fiscalAddress', null);
    const residenceTemplate = {
      stato: fiscalAddress?.CodiceNazione,
      localita: fiscalAddress?.Comune,
      provincia: fiscalAddress?.CodiceProvincia,
      cap: fiscalAddress?.Cap,
      indirizzo: fiscalAddress?.Indirizzo
    };
    const identificationDocument = CommonUtils.checkAndGet(outcome, 'document', null);
    const documentTemplate = {
      tipoDocumento: identificationDocument?.TipoDocumento,
      numeroDocumento: identificationDocument?.NumeroDocumento,
      dataRilascio: identificationDocument?.DataRilascio,
      enteRilascio: { value: identificationDocument?.EnteRilascio, disabled: true },
      comuneRilascio: identificationDocument?.LuogoRilascio
    };
    const emptyEffectiveOwner = {
      registryForm: FormUtils.initFormGroupFromTemplate(registryTemplate, effective_owner_registry_form_validators),
      residenceForm: FormUtils.initFormGroupFromTemplate(residenceTemplate, isv_contractor_residence_form_validators),
      identificationDocumentForm: FormUtils.initFormGroupFromTemplate(documentTemplate, document_change_form_validators),
      properVerificationForm: FormUtils.initFormGroupFromTemplate(isv_tutor_proper_verification_form_template, isv_tutor_proper_verification_form_validators)
    }
    this.effectiveOwnersList.push(emptyEffectiveOwner);
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  getEffectiveOwnerPanleTitle(index: number): string {
    return 'Dati del ' + effective_owner_positional_text[index] + ' titolare effettivo';
  }

  onActionRequested(event: EModalOutput, index: number): void {
    LoggerService.logInfo(this.constructor.name, 'onActionRequested', index);
    if (event === EModalOutput.REMOVE) {
      this.effectiveOwnersList.splice(index, 1);
    }
  }

  get showEffectiveOwnersSection(): boolean {
    return this._policyService.isLegalSubject;
  }

  get showInsuredSection(): boolean {
    return this.operationForm?.get('contractorNotInsured')?.value === 'SI'
  }

  get showBeneficiarySection(): boolean {
    return this.operationForm?.get('designedBeneficiary')?.value === 'SI'
  }

  get filteredBeneficiaryFormKeys(): IFormKey[] {
    return this.beneficiaryFormKeys?.filter((key) => {
      return !key.hidden;
    });
  }

  get isContractorChange(): boolean {
    return this._policyService.operationId === '_VCONT';
  }

  get isContractorLegalSubject(): boolean {
    return this._policyService.isLegalSubject;
  }

}
